const DEFAULT_FSRS_PARAMS = {
  requestRetention: 0.9,
  easyBonus: 1.3,
  hardFactor: 1.2,
  minIntervalMinutes: 10,
  lapseIntervalMinutes: 5,
  initialStability: 0.3,
  initialDifficulty: 5,
};

const MINUTES = 60 * 1000;
const DAYS = 24 * 60 * 60 * 1000;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function toDate(value, fallbackDate) {
  if (value instanceof Date) return value;
  if (value?.toDate) return value.toDate();
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return fallbackDate;
}

function mergeParams(fsrsParams) {
  return { ...DEFAULT_FSRS_PARAMS, ...(fsrsParams || {}) };
}

function nextIntervalMs(grade, stabilityDays, params) {
  if (grade === "again") return params.lapseIntervalMinutes * MINUTES;
  if (grade === "hard") return Math.max(params.minIntervalMinutes * MINUTES, stabilityDays * 0.6 * DAYS);
  if (grade === "good") return Math.max(params.minIntervalMinutes * MINUTES, stabilityDays * DAYS);
  return Math.max(params.minIntervalMinutes * MINUTES, stabilityDays * params.easyBonus * DAYS);
}

function adjustDifficulty(difficulty, grade) {
  if (grade === "again") return clamp(difficulty + 0.35, 1, 10);
  if (grade === "hard") return clamp(difficulty + 0.15, 1, 10);
  if (grade === "good") return clamp(difficulty - 0.05, 1, 10);
  return clamp(difficulty - 0.2, 1, 10);
}

function adjustStability(stabilityDays, difficulty, grade, params) {
  if (grade === "again") return params.initialStability;

  const difficultyFactor = clamp(1 + (5 - difficulty) * 0.06, 0.7, 1.35);
  const gradeFactor =
    grade === "hard" ? params.hardFactor :
    grade === "good" ? 1.6 :
    2.0;

  return clamp(stabilityDays * difficultyFactor * gradeFactor, 0.1, 36500);
}

export function defaultSrs(now = new Date()) {
  return {
    state: "new",
    dueAt: toDate(now, new Date()),
    reps: 0,
    lapses: 0,
    stability: DEFAULT_FSRS_PARAMS.initialStability,
    difficulty: DEFAULT_FSRS_PARAMS.initialDifficulty,
  };
}

export const defaultFsrsParams = DEFAULT_FSRS_PARAMS;

// TODO: Replace with a full FSRS implementation/library. Keep this API stable.
export function scheduleNext(cardSrs, grade, now = new Date(), fsrsParams = DEFAULT_FSRS_PARAMS) {
  const params = mergeParams(fsrsParams);
  const nowDate = toDate(now, new Date());
  const srs = cardSrs || defaultSrs(nowDate);

  const prevStability = typeof srs.stability === "number" ? srs.stability : params.initialStability;
  const prevDifficulty = typeof srs.difficulty === "number" ? srs.difficulty : params.initialDifficulty;

  const stability = adjustStability(prevStability, prevDifficulty, grade, params);
  const difficulty = adjustDifficulty(prevDifficulty, grade);
  const intervalMs = nextIntervalMs(grade, stability, params);

  const nextState =
    grade === "again"
      ? (srs.state === "review" ? "relearning" : "learning")
      : (srs.reps || 0) >= 1
        ? "review"
        : "learning";

  const dueAt = new Date(nowDate.getTime() + intervalMs);

  return {
    dueAt,
    nextSrs: {
      state: nextState,
      dueAt,
      lastReviewedAt: nowDate,
      stability,
      difficulty,
      reps: (srs.reps || 0) + 1,
      lapses: (srs.lapses || 0) + (grade === "again" ? 1 : 0),
    },
  };
}
